/**
 * API configuration utility
 * Automatically selects the appropriate server URL based on the environment
 * and provides helper functions for making authenticated API calls
 */

// Determine if we're running in production by checking if we're on localhost
const isLocalhost = Boolean(window.location.hostname === "localhost" || window.location.hostname === "[::1]" || window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/));

// Select the appropriate API URL based on environment
export const API_URL = isLocalhost ? process.env.REACT_APP_LOCAL_SERVER_URL : process.env.REACT_APP_LIVE_SERVER_URL;

// Helper function to build full API endpoint URLs
export const getApiUrl = (endpoint) => {
    return `${API_URL}/api${endpoint}`;
};

/**
 * Get the authentication token from localStorage
 * @returns {string} The authentication token
 */
export const getAuthToken = () => {
    return localStorage.getItem("token");
};

/**
 * Get headers for authenticated API requests
 * @param {boolean} includeContentType - Whether to include Content-Type header
 * @returns {Object} Headers object with Authorization and optional Content-Type
 */
export const getAuthHeaders = (includeContentType = true) => {
    const token = getAuthToken();
    const headers = {};

    if (token) {
        headers["Authorization"] = `Bearer ${token}`;
    }

    if (includeContentType) {
        headers["Content-Type"] = "application/json";
    }

    return headers;
};

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise} Fetch promise
 */
export const fetchWithAuth = async (endpoint, options = {}) => {
    const url = getApiUrl(endpoint);
    const headers = getAuthHeaders(true);

    const fetchOptions = {
        ...options,
        headers: {
            ...headers,
            ...options.headers,
        },
    };

    return fetch(url, fetchOptions);
};

/**
 * Make an authenticated POST request with JSON body
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request body data
 * @param {Object} options - Additional fetch options
 * @returns {Promise} Fetch promise
 */
export const postWithAuth = async (endpoint, data, options = {}) => {
    return fetchWithAuth(endpoint, {
        method: "POST",
        body: JSON.stringify(data),
        ...options,
    });
};
